import { Button, Footer } from '@gw/ui';
import { GitCommitHorizontal, Shield } from 'lucide-react';
import { hide, show, validConsent } from 'vanilla-cookieconsent';

import { useConsentModalOpen } from '@/components/cookie-consent/consentModalStore';
import { STORAGE_KEYS } from '@/constants/storageKeys';
import { isValidVersion } from '@/lib/version-bootstrap';

interface AppFooterProps {
  /** Classes for the <footer> element. Defaults to the shared border-t bar. */
  className?: string;
}

/**
 * The pinned sha from localStorage, or null when running 'latest'/unset.
 * GW-12470: the version-pin indicator now lives in the footer (see below)
 * rather than a fixed top banner that overlapped the header.
 */
function getPinnedSha(): string | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.CURRENT_VERSION);
    if (stored !== null && isValidVersion(stored) && stored !== 'latest') {
      return stored;
    }
  } catch (_) {
    // localStorage unavailable
  }
  return null;
}

/** Clear the pin and reload as 'latest', stripping any ?version=/?gwVersion=. */
function returnToLatest(): void {
  try {
    localStorage.setItem(STORAGE_KEYS.CURRENT_VERSION, 'latest');
  } catch (_) {
    // localStorage unavailable
  }
  const url = new URL(window.location.href);
  url.searchParams.delete('version');
  url.searchParams.delete('gwVersion');
  window.location.replace(url.toString());
}

/**
 * AppFooter — the single site footer bar shared by all layouts.
 *
 * Renders the @gw/ui Footer atom (copyright) plus a centered icon button
 * that toggles the cookie/privacy consent modal (vanilla-cookieconsent's
 * consent modal, which also links to the Privacy Policy and Terms of Service).
 * The icon is intentionally generic (shield = privacy), not a cookie, because
 * the modal covers privacy/legal broadly, not just cookies.
 *
 * Toggle behaviour (GDPR-safe): the shield closes the modal ONLY when it is
 * currently open AND the user has already made a consent choice
 * (`validConsent()`). Before any choice (first-run banner), the shield always
 * opens and never dismisses — we must never help dismiss the first-run banner
 * without a decision.
 */
export function AppFooter({ className = 'border-t px-6 py-4' }: AppFooterProps) {
  const isModalOpen = useConsentModalOpen();
  const pinnedSha = getPinnedSha();

  const handleToggle = () => {
    if (isModalOpen && validConsent()) {
      hide();
    } else {
      show(true);
    }
  };

  return (
    <footer className={className}>
      <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
        <Footer symbolClassName="text-primary" />
        <button
          type="button"
          onClick={handleToggle}
          aria-label="Privacy & cookie preferences"
          title="Privacy & cookie preferences"
          className="text-muted-foreground hover:text-foreground focus-visible:ring-ring cursor-pointer rounded-md p-1 transition-colors focus-visible:ring-2 focus-visible:outline-none"
        >
          <Shield className="h-5 w-5" aria-hidden="true" />
        </button>

        {/* GW-12470: version-pin indicator lives in the footer (was a fixed top
            banner that covered the header). Rendered only when a non-latest sha
            is pinned via ?version=/?gwVersion=. */}
        {pinnedSha ? (
          <span
            role="status"
            aria-live="polite"
            aria-label={`Viewing pinned version ${pinnedSha.slice(0, 7)}`}
            className="text-muted-foreground flex items-center gap-2 font-mono text-xs"
          >
            <GitCommitHorizontal className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
            <span>
              Viewing version{' '}
              <span className="text-foreground font-bold">{pinnedSha.slice(0, 7)}</span>
              {pinnedSha.length > 7 ? (
                <span className="opacity-60">{pinnedSha.slice(7)}</span>
              ) : null}
            </span>
            <Button
              variant="secondary"
              size="sm"
              onClick={returnToLatest}
              className="h-6 px-3 text-xs"
              aria-label="Return to latest version"
            >
              Return to latest
            </Button>
          </span>
        ) : null}
      </div>
    </footer>
  );
}
